import { Tearsheet } from '@carbon/ibm-products';
import { Loading } from '@carbon/react';
import { useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import {
    AnnouncementDto,
    useGetAnnouncementsQuery,
    useUpdateAnnouncementMutation,
} from 'api/announcementEndpoints';
import AnnouncementForm, {
    AnnouncementFormHandle,
    AnnouncementFormValues,
} from '../AnnouncementForm/AnnouncementForm';

import styles from './EditAnnouncementView.module.scss';

type Props = {
    announcement?: AnnouncementDto | null;
    open?: boolean;
    onClose?: () => void;
}

const EditAnnouncementView = ({
    announcement,
    open = true,
    onClose,
}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const currentEntityId = useGetCurrentEntityId();
    const [updateAnnouncement, { isLoading }] = useUpdateAnnouncementMutation();
    const formRef = useRef<AnnouncementFormHandle>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const initialValues = useMemo<Partial<AnnouncementFormValues> | undefined>(() => {
        if (!announcement) return undefined;

        return {
            title: announcement.title,
            content: announcement.content,
            publishedAt: announcement.publishedAt,
        };
    }, [announcement]);

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }

        navigate('/announcements');
    };

    const handleSubmit = async (values: AnnouncementFormValues) => {
        if (!currentEntityId || !announcement) return;
        setErrorMessage('');

        try {
            await updateAnnouncement({
                entityId: currentEntityId,
                id: announcement.id,
                data: {
                    id: announcement.id,
                    title: values.title,
                    content: values.content,
                    publishedAt: announcement.publishedAt,
                },
            }).unwrap();
            handleClose();
        } catch {
            setErrorMessage(intl.formatMessage(messages.submitError));
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={announcement?.title ?? intl.formatMessage(messages.title)}
                description={intl.formatMessage(messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={handleClose}
                selectorPrimaryFocus="#announcement-title"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: handleClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled || !announcement,
                        loading: isLoading,
                        onClick: () => formRef.current?.submit(),
                    },
                ]}
            >
                {open && announcement && (
                    <div className={styles.form}>
                        {errorMessage && (
                            <div className={styles.error}>{errorMessage}</div>
                        )}
                        <AnnouncementForm
                            key={announcement.id}
                            ref={formRef}
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            hideActions
                            onDisabledChange={setIsSubmitDisabled}
                        />
                    </div>
                )}
            </Tearsheet>
        </>
    );
};

const EditAnnouncementViewContainer = ({
    announcement: providedAnnouncement,
    open,
    onClose,
}: Props) => {
    const currentEntityId = useGetCurrentEntityId();
    const { id } = useParams();
    const { data, isFetching } = useGetAnnouncementsQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId || !!providedAnnouncement },
    );

    const announcement = useMemo(
        () => providedAnnouncement ?? (data ?? []).find((a) => a.id === id) ?? null,
        [data, id, providedAnnouncement],
    );

    if (isFetching) return <Loading />;

    return (
        <EditAnnouncementView
            announcement={announcement}
            open={open ?? true}
            onClose={onClose}
        />
    );
};

const messages = defineMessages({
    title: {
        id: 'announcements.edit.title',
        defaultMessage: 'Edytuj ogłoszenie',
    },
    description: {
        id: 'announcements.edit.description',
        defaultMessage: 'Edytuj dane ogłoszenia parafialnego.',
    },
    submitError: {
        id: 'announcements.edit.submitError',
        defaultMessage: 'Nie udało się zaktualizować ogłoszenia. Sprawdź dane i spróbuj ponownie.',
    },
});

export default EditAnnouncementViewContainer;
